import { getCurrentLangName, getFanPageUrl, getFlagByCountryCode, getStudentNickname } from "helpers/String.helper";
import React from "react";
import { getImageUrl } from "helpers/Image.helper";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import { useNavigate } from "react-router-dom";

interface StudentInfoPopupProps {
    onClose: () => void;
    info: any;
    isLoading: boolean;
    errorMessage: string;
    onFollow: (studentId: number) => Promise<void>;
    onUnfollow: (studentId: number) => Promise<void>;
    isOnNetworking: boolean;
}

const StudentInfoPopupComponent: React.FC<StudentInfoPopupProps> = ({ onClose, info, isLoading, errorMessage, onFollow, onUnfollow, isOnNetworking }) => {
    const navigate = useNavigate();
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 w-screen h-screen top-0 left-0 p-4 !m-0">
            <div className="bg-white rounded-2xl w-full max-w-2xl mx-auto relative overflow-hidden">
                {/* 닫기 버튼 - 팝업 안쪽 우상단에 고정 */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 w-6 h-6 flex items-center justify-center text-black bg-white/80 hover:bg-white/90 transition-colors z-10 rounded-full"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {isLoading ? (
                    <div className="flex flex-col items-center">
                        {/* 프로필 이미지 스켈레톤 */}
                        <div className="relative w-full mb-4">
                            <div className="w-full aspect-square bg-gray-200 animate-pulse relative">
                                {/* 하단 그라데이션 오버레이 스켈레톤 */}
                                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-300/70 via-gray-300/30 to-transparent"></div>
                                
                                {/* 프로필 정보 스켈레톤 */}
                                <div className="absolute bottom-4 left-4 right-4">
                                    <div className="flex justify-between items-end">
                                        {/* 좌측: 이름, 국적 스켈레톤 */}
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <div className="w-24 h-6 bg-gray-300 rounded animate-pulse"></div>
                                                <div className="w-6 h-6 bg-gray-300 rounded animate-pulse"></div>
                                            </div>
                                        </div>
                                        
                                        {/* 우측: 나이 스켈레톤 */}
                                        <div className="text-right">
                                            <div className="w-16 h-4 bg-gray-300 rounded animate-pulse"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 통계 영역 스켈레톤 */}
                        <div className="flex flex-col gap-4 mb-8 w-full px-4 py-6">
                            <div className="flex justify-center gap-4">
                                <div className="text-center">
                                    <div className="w-20 h-3 bg-gray-200 rounded animate-pulse mb-1"></div>
                                    <div className="w-12 h-5 bg-gray-200 rounded animate-pulse"></div>
                                </div>
                                <div className="text-center">
                                    <div className="w-20 h-3 bg-gray-200 rounded animate-pulse mb-1"></div>
                                    <div className="w-8 h-5 bg-gray-200 rounded animate-pulse"></div>
                                </div>
                                <div className="text-center">
                                    <div className="w-20 h-3 bg-gray-200 rounded animate-pulse mb-1"></div>
                                    <div className="w-8 h-5 bg-gray-200 rounded animate-pulse"></div>
                                </div>
                            </div>
                        </div>

                        {/* 버튼 영역 스켈레톤 */}
                        <div className="flex flex-row gap-4 w-full px-4 pb-4">
                            <div className="w-full h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                            <div className="w-full h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                        </div>
                    </div>
                ) : errorMessage ? (
                    <div className="flex flex-col items-center justify-center py-10 text-gray-500">
                        <p className="text-center">{errorMessage}</p>
                    </div>
                ) : (
                    <div className="flex flex-col items-center">
                        {/* 프로필 이미지 - 팝업 상단을 덮도록 배치 */}
                        <div className="relative w-full mb-4">
                            <div className="w-full aspect-square overflow-hidden relative">
                                {/* 배경 프로필 이미지 */}
                                <img
                                    src={getImageUrl(info?.profileImageInfo) || "/imgs/dummy/basic-img.png"}
                                    alt={getStudentNickname(info, null) || "지망생 프로필 배경"}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        const target = e.target as HTMLImageElement;
                                        target.src = "/imgs/dummy/basic-img.png";
                                    }}
                                />
                                
                                {/* 하단 그라데이션 오버레이 */}
                                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                                
                                {/* 프로필 정보 */}
                                <div className="absolute bottom-4 left-4 right-4 text-white">
                                    <div className="flex justify-between items-end">
                                        {/* 좌측: 이름, 국적 */}
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-lg font-bold">
                                                    {getStudentNickname(info, null) || "지망생"}
                                                </h3>
                                                {info?.countryInfo?.code && (
                                                    <span className="text-lg">
                                                        {getFlagByCountryCode(info?.countryInfo?.code)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* 우측: 나이 */}
                                        <div className="text-right">
                                            {info?.birthYear && (
                                                <span className="text-sm text-white/90">
                                                    {info.birthYear}년생
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>



                        {info?.isActiveOfPublicProfile &&
                            <div className="flex flex-col gap-4 mb-8 w-full px-4 py-6">
                                <div className="flex justify-center gap-4">
                                    <div className="text-center">
                                        <div className="text-gray-600 text-xs">{getLanguageValue(PAGE_TYPE.STUDENT, "studentInfoPopup.txtFollowing")} / {getLanguageValue(PAGE_TYPE.STUDENT, "studentInfoPopup.txtFollower")}</div>
                                        <div className="font-bold">{info?.followingCount || 0} / {info?.followerCount || 0}</div>

                                    </div>
                                    <div className="text-center">
                                        <div className="text-gray-600 text-xs">{getLanguageValue(PAGE_TYPE.STUDENT, "studentInfoPopup.txtFavoriteByAgency")}</div>
                                        <div className="font-bold">{info?.favoriteByAgencyCount || 0}</div>

                                    </div>
                                    <div className="text-center">
                                        <div className="text-gray-600 text-xs">{getLanguageValue(PAGE_TYPE.STUDENT, "studentInfoPopup.txtTotalChastingChat")}</div>
                                        <div className="font-bold">{info?.totalCastingChatCount || 0}</div>

                                    </div>
                                </div>
                            </div>
                        }

                        <div className="flex flex-row gap-4 w-full px-4 pb-4">
                            {info && !info.isMine && (
                                <>
                                    {info?.isActiveOfPublicProfile &&
                                        <button
                                            className="px-4 py-2 rounded-lg w-full text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
                                            onClick={() => {
                                                onClose();
                                                navigate(`/student/wannabe/${info.id}`);
                                            }}
                                        >
                                            {getLanguageValue(PAGE_TYPE.STUDENT, "studentInfoPopup.txtShowDetail")}
                                        </button>
                                    }
                                    <button
                                        className={`px-4 py-2 rounded-lg text-sm font-medium w-full
                                        ${info.isFollowing
                                                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                : 'bg-[#CF3688] text-white hover:bg-[#CF3688]/90'}`}
                                        onClick={() => info.isFollowing ? onUnfollow(info.id) : onFollow(info.id)}
                                        disabled={isOnNetworking}
                                    >
                                        {!info.isFollowing && info.isFollower
                                            ? getLanguageValue(PAGE_TYPE.STUDENT, "studentInfoPopup.txtFollowBack")
                                            : info.isFollowing
                                                ? getLanguageValue(PAGE_TYPE.STUDENT, "studentInfoPopup.txtUnfollow")
                                                : getLanguageValue(PAGE_TYPE.STUDENT, "studentInfoPopup.txtFollow")}
                                    </button>
                                </>
                            )}
                        </div>

                        {/* <div className="text-center">
                            <a
                                href={getFanPageUrl(info)}
                                className="!text-[#CF3688] hover:underline text-sm"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {getLanguageValue(PAGE_TYPE.STUDENT, "studentInfoPopup.txtFanPageVisit")}
                            </a>
                        </div> */}
                    </div>
                )}
            </div>
        </div>
    );
};

export default StudentInfoPopupComponent;